import { fetchClientPerformance } from '../../services/vectorDashboardApi';

export const BROKER_STRATEGIES = new Set(['Coinbase', 'Interactive Brokers', 'Alpaca', 'Ninja Trader']);

export const DATE_RANGE_EMPTY_LABELS = {
  '1M': 'the last 30 days',
  '6M': 'the last 6 months',
  YTD: 'year to date',
  '1Y': 'the last 12 months',
  All: 'available history',
  Custom: 'the selected date range',
};

export function getEmptyRangeLabel(dateRange) {
  if (typeof dateRange === 'object' && dateRange.start && dateRange.end) {
    return `between ${dateRange.start} and ${dateRange.end}`;
  }
  return DATE_RANGE_EMPTY_LABELS[dateRange] || 'the selected period';
}

function extractNinjaAccountNumber(accountKey, displayName) {
  const raw = String(displayName || accountKey || '').trim();
  const numMatch = raw.match(/(\d{4,})/);
  if (numMatch) return numMatch[1];
  if (/^\d+$/.test(String(accountKey))) return String(accountKey);
  return String(accountKey || 'Account');
}

/** 'live' | 'demo' (any case) -> 'Live' | 'Demo'; null otherwise. */
function ninjaModeLabel(mode) {
  const value = String(mode || '').trim().toLowerCase();
  if (value === 'demo') return 'Demo';
  if (value === 'live') return 'Live';
  return null;
}

/** Portfolio / picker label without trailing "(owner name)". */
export function brokerAccountDisplayName(client) {
  if (!client) return '';
  const name = String(client.name || '').trim();
  const broker = String(client.broker || '').trim();
  const shortName = name.replace(/\s*\([^()]*\)\s*$/, '').trim();

  if (broker === 'Ninja Trader') {
    const accountKey = String(client.dataSeriesName || '').split('|')[2] || '';
    if (accountKey === 'demo') return 'NinjaTrader - Demo';
    if (accountKey === 'live') return 'NinjaTrader - Live';

    const num = extractNinjaAccountNumber(accountKey, shortName);
    // Prefer the server-supplied mode; fall back to whatever the label already
    // carries so this keeps working if `mode` is ever absent.
    const mode = ninjaModeLabel(client.mode) || ninjaModeLabel(shortName.match(/·\s*(live|demo)\b/i)?.[1]);
    return mode ? `NinjaTrader - ${num} · ${mode}` : `NinjaTrader - ${num}`;
  }

  return shortName || broker || name;
}

/** Account picker label — short broker name plus owner email/name in parentheses. */
export function brokerAccountPickerLabel(client) {
  if (!client) return '';
  const name = String(client.name || '').trim();
  const ownerMatch = name.match(/\(([^()]+)\)\s*$/);
  const owner = ownerMatch ? ownerMatch[1].trim() : '';
  const shortName = brokerAccountDisplayName(client);
  return owner ? `${shortName} (${owner})` : (name || shortName);
}

/** Broker series name format: BROKER|userId|accountKey */
export function parseTargetUserId(client) {
  if (client?.targetUserId != null) return String(client.targetUserId);
  const parts = String(client?.dataSeriesName || '').split('|');
  return parts.length >= 2 ? String(parts[1]) : null;
}

function isBrokerPortfolio(client, valueType) {
  return valueType === 'equity'
    || BROKER_STRATEGIES.has(client?.broker)
    || BROKER_STRATEGIES.has(client?.strategy);
}

export function buildClientChartPoint(date, seriesValues) {
  return { date, ...seriesValues };
}

export async function fetchMergedClientPerformance(clients, dateRange) {
  if (!clients.length) {
    return { chartData: [], seriesRaw: {}, metrics: {}, seriesQuality: {}, hasData: false };
  }

  const responses = await Promise.all(
    clients.map(async (client) => {
      const res = await fetchClientPerformance(client.dataSeriesName, dateRange);
      return { client, res };
    }),
  );

  const metrics = {};
  const seriesQuality = {};
  const seriesRaw = {};
  const dateMap = new Map();
  let hasData = false;

  for (const { client, res } of responses) {
    const seriesName = client.name;
    const brokerPortfolio = isBrokerPortfolio(client, res.valueType);
    metrics[seriesName] = res.metrics || {};
    seriesQuality[seriesName] = res.dataQuality || null;

    for (const point of res.data || []) {
      hasData = true;
      const value = brokerPortfolio ? point.value : 100000 * (1 + point.value / 100);
      const existing = dateMap.get(point.date) || { date: point.date };
      existing[seriesName] = value;
      dateMap.set(point.date, existing);

      // Raw equity + external flow, kept in series order (server sorts ascending)
      // so the chart-tab transforms can chain returns and cumulate P&L.
      //
      // One point per DATE, last wins — the same collapse `dateMap` above
      // performs for chartData. A broker returns one point per closed trade, so
      // two trades on one day produced two points here and one there: the
      // sparkline and the chart were drawn from different series and disagreed
      // about the same account. Last wins because these are cumulative equity
      // values, so the final trade of a day is that day's closing equity.
      const bucket = (seriesRaw[seriesName] ||= []);
      const rawPoint = {
        date: point.date,
        value,
        netFlow: brokerPortfolio ? (Number(point.netFlow) || 0) : 0,
      };
      const last = bucket[bucket.length - 1];
      if (last && last.date === point.date) {
        // Flows are per-event, not cumulative — summing keeps the day's total.
        rawPoint.netFlow += last.netFlow;
        bucket[bucket.length - 1] = rawPoint;
      } else {
        bucket.push(rawPoint);
      }
    }
  }

  const chartData = Array.from(dateMap.values()).sort((a, b) => a.date.localeCompare(b.date));
  return { chartData, seriesRaw, metrics, seriesQuality, hasData };
}
